import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { cn } from "@/utils/cn";

type ScrollFadeProps = {
  children: ReactNode;
  className?: string;
};

/** Scrollable area with a fade on each edge that still has content beyond it. */
export function ScrollFade({ children, className }: ScrollFadeProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [fades, setFades] = useState({ bottom: false, top: false });

  const updateFades = useCallback(() => {
    const scroller = scrollerRef.current;

    if (!scroller) return;
    const { clientHeight, scrollHeight, scrollTop } = scroller;

    setFades({
      bottom: scrollTop + clientHeight < scrollHeight - 1,
      top: scrollTop > 0,
    });
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller) return;
    const observer = new ResizeObserver(updateFades);

    observer.observe(scroller);
    for (const child of scroller.children) observer.observe(child);
    updateFades();

    return () => observer.disconnect();
  }, [updateFades, children]);

  return (
    <div className={cn("relative flex min-h-0 flex-col", className)}>
      <div
        className="min-h-0 overflow-y-auto"
        onScroll={updateFades}
        ref={scrollerRef}
      >
        {children}
      </div>
      {fades.top ? (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-bg-primary via-bg-primary/70 to-transparent shadow-[inset_0_8px_8px_-8px_rgb(16_24_40/0.12)]"
          data-testid="scroll-fade-top"
        />
      ) : null}
      {fades.bottom ? (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-bg-primary via-bg-primary/70 to-transparent shadow-[inset_0_-8px_8px_-8px_rgb(16_24_40/0.12)]"
          data-testid="scroll-fade-bottom"
        />
      ) : null}
    </div>
  );
}
